import { create } from 'zustand';
import type { AccessRestrictionReason } from '@/lib/access-restriction';

/**
 * Holds the reason the server last refused this workspace, so the app can show
 * the access-restriction screen instead of a raw error.
 *
 * Deliberately **not** persisted, unlike `sp-auth`. This is a transient
 * presentation flag, and persisting it would let a stale value outlive the
 * restriction that produced it — a tenant reactivated overnight would come back
 * to a locked screen until they cleared site data. Losing it on refresh is
 * harmless: the page falls back to the validated `?reason=` param, and the very
 * next API call re-establishes the truth from the server.
 *
 * It is never consulted to decide whether a request may be made. `apiClient`
 * writes to it; nothing reads it to gate anything.
 */
interface AccessRestrictionState {
  reason: AccessRestrictionReason | null;
  /** Recorded from a server 403 carrying a known restriction code. */
  restrict: (reason: AccessRestrictionReason) => void;
  /** Cleared on sign-out and on a fresh sign-in, so the next session starts clean. */
  clear: () => void;
}

export const useAccessRestrictionStore = create<AccessRestrictionState>()((set) => ({
  reason: null,
  restrict: (reason) => set({ reason }),
  clear: () => set({ reason: null }),
}));
